import React, { useEffect, useState } from "react";
import { ukDayYear as fmtDate } from "@/lib/time";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileStack, FileDown, Loader2 } from "lucide-react";


/**
 * PartnerPaperworkCard — the partner's own contracts that Connexis has signed, sitting
 * alongside the Connexis-issued agreement and NDA. Hidden entirely when there are none.
 */
export function PartnerPaperworkCard() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/portal/partner-docs").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);

  const download = async (d) => {
    setBusy(d.id);
    try {
      const r = await api.get(`/portal/partner-docs/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: d.content_type }));
      const a = document.createElement("a"); a.href = url; a.download = d.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Download failed — please try again"); }
    finally { setBusy(null); }
  };

  if (!items || items.length === 0) return null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="partner-paperwork-card">
      <div className="flex items-center gap-2.5">
        <span className="grid place-items-center size-10 rounded-xl border border-slate-200 bg-slate-50 text-slate-600">
          <FileStack className="size-4.5" />
        </span>
        <div>
          <div className="text-[15px] font-semibold text-slate-900">Your own paperwork, countersigned</div>
          <div className="text-[12px] text-slate-500">Documents you sent us, signed by Connexis and kept on file for you.</div>
        </div>
      </div>
      <div className="mt-4 space-y-1.5">
        {items.map((d) => (
          <div key={d.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/50 p-3"
               data-testid={`partner-paperwork-${d.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-slate-900 truncate">{d.title}</div>
              <div className="text-[11px] text-slate-500">
                {d.doc_type}{d.signed_on ? ` · signed ${d.signed_on}` : ""} · added {fmtDate(d.uploaded_at)}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => download(d)} disabled={busy === d.id}
                    className="rounded-full h-9 text-[12.5px] shrink-0" data-testid={`partner-paperwork-download-${d.id}`}>
              {busy === d.id ? <Loader2 className="size-3.5 animate-spin" /> : <><FileDown className="size-3.5 mr-1.5" /> Download</>}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
